export interface AnchorCandidate {
    uid: string;
    rect: { top: number; bottom: number };
}

/**
 * Determines which issue in the tasklist should be persisted as the scroll
 * anchor: the topmost item that fills at least 50% of the smaller of (a) its
 * own height or (b) the container's height — a rect-scan against the actual
 * scroll container instead of an IntersectionObserver (no async callback
 * delay, no observer lifecycle to wire up/tear down), keyed by the issue's
 * uid instead of its list index so a persisted anchor still resolves
 * correctly after the list has changed (items added/removed shift indices,
 * never a stable uid). Divider elements (`<hr>`) never appear in the
 * candidate list the caller builds — they carry no uid.
 */
export class TasklistScrollAnchorHelper {

    public static readonly VISIBILITY_THRESHOLD = 0.5;

    /**
     * `candidates` MUST already be given in top-to-bottom (DOM/index) order —
     * the caller (the virtualized list) satisfies this naturally. Iterates
     * lazily and returns on the first qualifying candidate, so an `Iterable`
     * is accepted rather than requiring a pre-built array.
     */
    public static findTopmostAtLeastHalfVisible(
        candidates: Iterable<AnchorCandidate>,
        containerRect: { top: number; bottom: number },
    ): string | null {
        const containerHeight = containerRect.bottom - containerRect.top;

        for (const candidate of candidates) {
            const height = candidate.rect.bottom - candidate.rect.top;
            const visibleTop = Math.max(candidate.rect.top, containerRect.top);
            const visibleBottom = Math.min(candidate.rect.bottom, containerRect.bottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            // Clamped against the container's own height: an item taller
            // than the container can never show >=50% of ITS OWN height,
            // even while filling the container edge-to-edge — without the
            // clamp, such an item could never qualify as the anchor at all.
            const qualifyingHeight = Math.min(height, containerHeight);

            if (qualifyingHeight > 0 && visibleHeight / qualifyingHeight >= TasklistScrollAnchorHelper.VISIBILITY_THRESHOLD) {
                return candidate.uid;
            }
        }

        return null;
    }
}
